import { inject, Injectable, Provider } from '@angular/core';
import {
  AnonymousLandingPageConfigurationDto, AuthorizedLandingPageConfigurationDto, BrandPageConfigurationDto, CheckoutConfigurationDto, ConfigurationDto,
  ConfigurationGetQueryResult, ELogLevel, EShopState, LogBackendConfigurationDto, ProductListConfigurationDto, SearchConfigurationDto, VersionDto
} from '../../INTEGRA_Shop_Client';
import { Observable, map, of, tap } from 'rxjs';
import { IConfigurationService, IConfigurationServiceToken } from './configuration-service.interface';
import { IConfigurationApiService, IConfigurationApiServiceToken } from '../../api/configuration/configuration-api-service.interface';
import { LoggerService } from '../logger/logger.service';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { IComparisonService, IComparisonServiceToken } from '../comparison/comparison-service.interface';
import { ILoggerServiceToken } from '../logger/logger-service.interface';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService implements IConfigurationService {
  private readonly logger: LoggerService;
  private readonly configurationApiService: IConfigurationApiService;
  private readonly comparisonService: IComparisonService;
  private dynamicConfiguration: ConfigurationGetQueryResult | undefined;

  public constructor() {
    this.logger = inject(ILoggerServiceToken);
    this.configurationApiService = inject(IConfigurationApiServiceToken);
    this.comparisonService = inject(IComparisonServiceToken);
  }

  private dynamicConfigurationGet(): Observable<ConfigurationGetQueryResult> {
    if (this.dynamicConfiguration != undefined) {
      return of(this.dynamicConfiguration);
    }

    this.logger.info("dynamicConfigurationGet uninitialized called", this.constructor.name);
    return this.configurationApiService.configurationGet(false).pipe(
      tap((result: ConfigurationGetQueryResult) => {
        this.dynamicConfiguration = result;
      })
    );
  }

  public dynamicConfigurationLoad(): void {
    this.logger.info("dynamicConfigurationLoad called", this.constructor.name);
    this.configurationApiService.configurationGet(false).subscribe(
      (configuration) => {
        this.handleVersionData(this.dynamicConfiguration?.version, configuration.version);
        this.dynamicConfiguration = configuration;
      }
    );
  }

  public getFullConfigurations(): Observable<ConfigurationDto | undefined> {
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        let anonymousLandingPage = new AnonymousLandingPageConfigurationDto({
          routerlink: dynamicConfiguration.configuration?.anonymousLandingPage?.routerlink != null ? dynamicConfiguration.configuration?.anonymousLandingPage.routerlink : ENVIRONMENT.anonymousLandingPage.routerlink.toString(),
          queryParams: dynamicConfiguration.configuration?.anonymousLandingPage?.queryParams != undefined || null ? dynamicConfiguration.configuration?.anonymousLandingPage?.queryParams : (ENVIRONMENT.anonymousLandingPage.queryParams != null ? new URLSearchParams(ENVIRONMENT.anonymousLandingPage.queryParams).toString() : undefined)
        });

        let authorizedLandingPage = new AuthorizedLandingPageConfigurationDto({
          routerlink: dynamicConfiguration.configuration?.authorizedLandingPage?.routerlink != null ? dynamicConfiguration.configuration?.authorizedLandingPage.routerlink : ENVIRONMENT.authorizedLandingPage.routerlink.toString(),
          queryParams: dynamicConfiguration.configuration?.authorizedLandingPage?.queryParams != undefined || null ? dynamicConfiguration.configuration?.authorizedLandingPage?.queryParams : (ENVIRONMENT.authorizedLandingPage.queryParams != null ? new URLSearchParams(ENVIRONMENT.authorizedLandingPage.queryParams).toString() : undefined)
        });

        let brandPage = new BrandPageConfigurationDto({
          routerlink: dynamicConfiguration.configuration?.brandPage?.routerlink != null ? dynamicConfiguration.configuration?.brandPage.routerlink : ENVIRONMENT.brandPage.routerlink.toString(),
          queryParams: dynamicConfiguration.configuration?.brandPage?.queryParams != undefined || null ? dynamicConfiguration.configuration?.brandPage?.queryParams : (ENVIRONMENT.brandPage.queryParams != null ? new URLSearchParams(ENVIRONMENT.brandPage.queryParams).toString() : undefined)
        });

        let checkout = new CheckoutConfigurationDto({
          expirationTimeInMinutes: dynamicConfiguration.configuration?.checkout?.expirationTimeInMinutes != null ? dynamicConfiguration.configuration?.checkout?.expirationTimeInMinutes : ENVIRONMENT.checkout.expirationTimeInMinutes,
          isCartInquiryCheckoutAllowed: dynamicConfiguration.configuration?.checkout?.isCartInquiryCheckoutAllowed != null ? dynamicConfiguration.configuration?.checkout?.isCartInquiryCheckoutAllowed : ENVIRONMENT.checkout.isCartInquiryCheckoutAllowed,
          isGuestOrderCheckoutAllowed: dynamicConfiguration.configuration?.checkout?.isGuestOrderCheckoutAllowed != null ? dynamicConfiguration.configuration?.checkout?.isGuestOrderCheckoutAllowed : ENVIRONMENT.checkout.isGuestOrderCheckoutAllowed,
          isOfferCheckoutAllowed: dynamicConfiguration.configuration?.checkout?.isOfferCheckoutAllowed != null ? dynamicConfiguration.configuration?.checkout?.isOfferCheckoutAllowed : ENVIRONMENT.checkout.isOfferCheckoutAllowed,
          isOrderCheckoutAllowed: dynamicConfiguration.configuration?.checkout?.isOrderCheckoutAllowed != null ? dynamicConfiguration.configuration?.checkout?.isOrderCheckoutAllowed : ENVIRONMENT.checkout.isOrderCheckoutAllowed,
          maximumOrderAmount: dynamicConfiguration.configuration?.checkout?.maximumOrderAmount != null ? dynamicConfiguration.configuration?.checkout?.maximumOrderAmount : ENVIRONMENT.checkout.maximumOrderAmount,
        });

        let search = new SearchConfigurationDto({
          categoryFilterInitialDisplayCount: dynamicConfiguration.configuration?.search?.categoryFilterInitialDisplayCount != null ? dynamicConfiguration.configuration?.search?.categoryFilterInitialDisplayCount : ENVIRONMENT.search.categoryFilterInitialDisplayCount,
          productFeatureFilterExpressionInitialDisplayCount: dynamicConfiguration.configuration?.search?.productFeatureFilterExpressionInitialDisplayCount != null ? dynamicConfiguration.configuration?.search?.productFeatureFilterExpressionInitialDisplayCount : ENVIRONMENT.search.productFeatureFilterExpressionInitialDisplayCount,
          searchPreviewMaxCount: dynamicConfiguration.configuration?.search?.searchPreviewMaxCount != null ? dynamicConfiguration.configuration?.search?.searchPreviewMaxCount : ENVIRONMENT.search.searchPreviewMaxCount,
          searchTextMinLengthInCharacter: dynamicConfiguration.configuration?.search?.searchTextMinLengthInCharacter != null ? dynamicConfiguration.configuration?.search?.searchTextMinLengthInCharacter : ENVIRONMENT.search.searchTextMinLengthInCharacter,
          techGroupFilterInitialDisplayCount: dynamicConfiguration.configuration?.search?.techGroupFilterInitialDisplayCount != null ? dynamicConfiguration.configuration?.search?.techGroupFilterInitialDisplayCount : ENVIRONMENT.search.techGroupFilterInitialDisplayCount,
        });

        let productList = new ProductListConfigurationDto({
          productListPageContentCount: dynamicConfiguration.configuration?.productList?.productListPageContentCount != null ? dynamicConfiguration.configuration?.productList?.productListPageContentCount : ENVIRONMENT.productList.productListPageContentCount,
          shouldDisplayAddListToCartButtonInList: dynamicConfiguration.configuration?.productList?.shouldDisplayAddListToCartButtonInList != null ? dynamicConfiguration.configuration?.productList?.shouldDisplayAddListToCartButtonInList : ENVIRONMENT.productList.shouldDisplayAddListToCartButtonInList,
        });

        let logbackend = new LogBackendConfigurationDto({
          logToBackend: dynamicConfiguration.configuration?.logbackend?.logToBackend != undefined || null ? dynamicConfiguration.configuration?.logbackend?.logToBackend : ENVIRONMENT.logBackend.logToBackend,
          classList: dynamicConfiguration.configuration?.logbackend?.classList != undefined || null ? dynamicConfiguration.configuration?.logbackend?.classList : ENVIRONMENT.logBackend.classList,
        });

        return new ConfigurationDto({
          anonymousLandingPage: anonymousLandingPage,
          authorizedLandingPage: authorizedLandingPage,
          brandPage: brandPage,
          checkout: checkout,
          search: search,
          productList: productList,
          logbackend: logbackend,
          externalFallbackURL: dynamicConfiguration.configuration?.externalFallbackURL != null ? dynamicConfiguration.configuration?.externalFallbackURL : ENVIRONMENT.externalFallbackURL,
          logLevel: dynamicConfiguration.configuration?.logLevel != null ? dynamicConfiguration.configuration?.logLevel : ENVIRONMENT.logLevel,
          shopState: dynamicConfiguration.configuration?.shopState != null ? dynamicConfiguration.configuration?.shopState : EShopState.Online,
          shouldDisplayCategoryBanner: dynamicConfiguration.configuration?.shouldDisplayCategoryBanner != null ? dynamicConfiguration.configuration?.shouldDisplayCategoryBanner : ENVIRONMENT.shouldDisplayCategoryBanner,
          shouldRemoveProductFromCartUponNegativeQuantity: dynamicConfiguration.configuration?.shouldRemoveProductFromCartUponNegativeQuantity != null ? dynamicConfiguration.configuration?.shouldRemoveProductFromCartUponNegativeQuantity : ENVIRONMENT.shouldRemoveProductFromCartUponNegativeQuantity,
          useAgeVerification: dynamicConfiguration.configuration?.useAgeVerification != null ? dynamicConfiguration.configuration?.useAgeVerification : ENVIRONMENT.useAgeVerification,
          logToConsole: dynamicConfiguration.configuration?.logToConsole != null ? dynamicConfiguration.configuration?.logToConsole : ENVIRONMENT.logToConsole,
        });
      })
    );
  }

  public logBackendGet(): Observable<LogBackendConfigurationDto> {
    this.logger.info("logBackanegGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return new LogBackendConfigurationDto({
          logToBackend: dynamicConfiguration.configuration?.logbackend?.logToBackend != undefined || null ? dynamicConfiguration.configuration?.logbackend?.logToBackend : ENVIRONMENT.logBackend.logToBackend,
          classList: dynamicConfiguration.configuration?.logbackend?.classList != undefined || null ? dynamicConfiguration.configuration?.logbackend?.classList : ENVIRONMENT.logBackend.classList,
        });
      })
    );
  }

  public authorizedLandingPageGet(): Observable<AuthorizedLandingPageConfigurationDto> {
    this.logger.info("authorizedLandingPageGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return new AuthorizedLandingPageConfigurationDto({
          routerlink: dynamicConfiguration.configuration?.authorizedLandingPage?.routerlink != null ? dynamicConfiguration.configuration?.authorizedLandingPage.routerlink : ENVIRONMENT.authorizedLandingPage.routerlink.toString(),
          queryParams: dynamicConfiguration.configuration?.authorizedLandingPage?.queryParams != undefined || null ? dynamicConfiguration.configuration?.authorizedLandingPage?.queryParams : (ENVIRONMENT.authorizedLandingPage.queryParams != null ? new URLSearchParams(ENVIRONMENT.authorizedLandingPage.queryParams).toString() : undefined)
        });
      })
    );
  }

  public brandPageGet(): Observable<BrandPageConfigurationDto> {
    this.logger.info("brandPageGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return new BrandPageConfigurationDto({
          routerlink: dynamicConfiguration.configuration?.brandPage?.routerlink != null ? dynamicConfiguration.configuration?.brandPage.routerlink : ENVIRONMENT.brandPage.routerlink.toString(),
          queryParams: dynamicConfiguration.configuration?.brandPage?.queryParams != undefined || null ? dynamicConfiguration.configuration?.brandPage?.queryParams : (ENVIRONMENT.brandPage.queryParams != null ? new URLSearchParams(ENVIRONMENT.brandPage.queryParams).toString() : undefined)
        });
      })
    );
  }

  public anonymousLandingPageGet(): Observable<AnonymousLandingPageConfigurationDto> {
    this.logger.info("anonymousLandingPageGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return new AnonymousLandingPageConfigurationDto({
          routerlink: dynamicConfiguration.configuration?.anonymousLandingPage?.routerlink != null ? dynamicConfiguration.configuration?.anonymousLandingPage.routerlink : ENVIRONMENT.anonymousLandingPage.routerlink.toString(),
          queryParams: dynamicConfiguration.configuration?.anonymousLandingPage?.queryParams != undefined || null ? dynamicConfiguration.configuration?.anonymousLandingPage?.queryParams : (ENVIRONMENT.anonymousLandingPage.queryParams != null ? new URLSearchParams(ENVIRONMENT.anonymousLandingPage.queryParams).toString() : undefined)
        });
      })
    );
  }

  public checkoutConfigurationGet(): Observable<CheckoutConfigurationDto> {
    this.logger.info("checkoutConfigurationGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return new CheckoutConfigurationDto({
          expirationTimeInMinutes: dynamicConfiguration.configuration?.checkout?.expirationTimeInMinutes != null ? dynamicConfiguration.configuration?.checkout?.expirationTimeInMinutes : ENVIRONMENT.checkout.expirationTimeInMinutes,
          isCartInquiryCheckoutAllowed: dynamicConfiguration.configuration?.checkout?.isCartInquiryCheckoutAllowed != null ? dynamicConfiguration.configuration?.checkout?.isCartInquiryCheckoutAllowed : ENVIRONMENT.checkout.isCartInquiryCheckoutAllowed,
          isGuestOrderCheckoutAllowed: dynamicConfiguration.configuration?.checkout?.isGuestOrderCheckoutAllowed != null ? dynamicConfiguration.configuration?.checkout?.isGuestOrderCheckoutAllowed : ENVIRONMENT.checkout.isGuestOrderCheckoutAllowed,
          isOfferCheckoutAllowed: dynamicConfiguration.configuration?.checkout?.isOfferCheckoutAllowed != null ? dynamicConfiguration.configuration?.checkout?.isOfferCheckoutAllowed : ENVIRONMENT.checkout.isOfferCheckoutAllowed,
          isOrderCheckoutAllowed: dynamicConfiguration.configuration?.checkout?.isOrderCheckoutAllowed != null ? dynamicConfiguration.configuration?.checkout?.isOrderCheckoutAllowed : ENVIRONMENT.checkout.isOrderCheckoutAllowed,
          maximumOrderAmount: dynamicConfiguration.configuration?.checkout?.maximumOrderAmount != null ? dynamicConfiguration.configuration?.checkout?.maximumOrderAmount : ENVIRONMENT.checkout.maximumOrderAmount,
        });
      })
    );
  }

  public searchConfigurationGet(): Observable<SearchConfigurationDto> {
    this.logger.info("searchConfigurationGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return new SearchConfigurationDto({
          categoryFilterInitialDisplayCount: dynamicConfiguration.configuration?.search?.categoryFilterInitialDisplayCount != null ? dynamicConfiguration.configuration?.search?.categoryFilterInitialDisplayCount : ENVIRONMENT.search.categoryFilterInitialDisplayCount,
          productFeatureFilterExpressionInitialDisplayCount: dynamicConfiguration.configuration?.search?.productFeatureFilterExpressionInitialDisplayCount != null ? dynamicConfiguration.configuration?.search?.productFeatureFilterExpressionInitialDisplayCount : ENVIRONMENT.search.productFeatureFilterExpressionInitialDisplayCount,
          searchPreviewMaxCount: dynamicConfiguration.configuration?.search?.searchPreviewMaxCount != null ? dynamicConfiguration.configuration?.search?.searchPreviewMaxCount : ENVIRONMENT.search.searchPreviewMaxCount,
          searchTextMinLengthInCharacter: dynamicConfiguration.configuration?.search?.searchTextMinLengthInCharacter != null ? dynamicConfiguration.configuration?.search?.searchTextMinLengthInCharacter : ENVIRONMENT.search.searchTextMinLengthInCharacter,
          techGroupFilterInitialDisplayCount: dynamicConfiguration.configuration?.search?.techGroupFilterInitialDisplayCount != null ? dynamicConfiguration.configuration?.search?.techGroupFilterInitialDisplayCount : ENVIRONMENT.search.techGroupFilterInitialDisplayCount,
        });
      })
    );
  }

  public productListConfigurationGet(): Observable<ProductListConfigurationDto> {
    this.logger.info("productListConfigurationGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return new ProductListConfigurationDto({
          productListPageContentCount: dynamicConfiguration.configuration?.productList?.productListPageContentCount != null ? dynamicConfiguration.configuration?.productList?.productListPageContentCount : ENVIRONMENT.productList.productListPageContentCount,
          shouldDisplayAddListToCartButtonInList: dynamicConfiguration.configuration?.productList?.shouldDisplayAddListToCartButtonInList != null ? dynamicConfiguration.configuration?.productList?.shouldDisplayAddListToCartButtonInList : ENVIRONMENT.productList.shouldDisplayAddListToCartButtonInList,
        });
      })
    );
  }

  public externalFallbackURLGet(): Observable<string | undefined> {
    this.logger.info("externalFallbackURLGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return dynamicConfiguration.configuration?.externalFallbackURL != null ? dynamicConfiguration.configuration?.externalFallbackURL : ENVIRONMENT.externalFallbackURL;
      })
    );
  }

  public shouldDisplayCategoryBannerGet(): Observable<boolean | undefined> {
    this.logger.info("shouldDisplayCategoryBannerGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return dynamicConfiguration.configuration?.shouldDisplayCategoryBanner != null ? dynamicConfiguration.configuration?.shouldDisplayCategoryBanner : ENVIRONMENT.shouldDisplayCategoryBanner;
      })
    );
  }

  public shouldRemoveProductFromCartUponNegativeQuantityGet(): Observable<boolean | undefined> {
    this.logger.info("shouldRemoveProductFromCartUponNegativeQuantityGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return dynamicConfiguration.configuration?.shouldRemoveProductFromCartUponNegativeQuantity != null ? dynamicConfiguration.configuration?.shouldRemoveProductFromCartUponNegativeQuantity : ENVIRONMENT.shouldRemoveProductFromCartUponNegativeQuantity;
      })
    );
  }

  public useAgeVerificicationGet(): Observable<boolean | undefined> {
    this.logger.info("useAgeVerificicationGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return dynamicConfiguration.configuration?.useAgeVerification != null ? dynamicConfiguration.configuration?.useAgeVerification : ENVIRONMENT.useAgeVerification;
      })
    );
  }

  public logLevelGet(): Observable<ELogLevel | undefined> {
    this.logger.info("logLevelGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return dynamicConfiguration.configuration?.logLevel != null ? dynamicConfiguration.configuration.logLevel : ENVIRONMENT.logLevel;
      })
    );
  }

  public shopStateGet(): Observable<EShopState | undefined> {
    this.logger.info("shopStateGet called", this.constructor.name);
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return dynamicConfiguration.configuration?.shopState != null ? dynamicConfiguration.configuration.shopState : EShopState.Online;
      })
    );
  }

  public shopStateGetOrUndefined(): EShopState | undefined {
    this.logger.info("shopStateGetOrUndefined called", this.constructor.name);
    return this.dynamicConfiguration?.configuration?.shopState;
  }

  private handleVersionData(versionStored: VersionDto | undefined, versionNew: VersionDto | undefined) {
    let versionsAreEqual = this.comparisonService.equalsGenericDeepSlow(versionStored, versionNew);
    // ToDo: handle info / update / refresh
  }

  public logToConsoleGet(): Observable<boolean | undefined> {
    return this.dynamicConfigurationGet().pipe(
      map((dynamicConfiguration) => {
        return dynamicConfiguration.configuration?.logToConsole != null ? dynamicConfiguration.configuration.logToConsole : ENVIRONMENT.logToConsole;
      })
    );
  }
}


/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const ConfigurationServiceProvider: Provider[] = [
  { provide: IConfigurationServiceToken, useExisting: ConfigurationService }
];
